import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { ALL_PRODUCTS_LABEL_ID, BI, PAGES } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { PageData } from '../PageTemplate';
import {
  PageType,
  RoadmapFeatureResolution,
  RoadmapFilterType,
} from '../../types';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { pageView } from '@wix/bi-logger-new-help-center/v2';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { FilterToName, sortTypeToName, tabToName } from '../../utils/roadmap';
import { ArticleSortType } from '@wix/answers-api';

type PageViewProps = {
  pageType: PageType;
  pageData: PageData;
  itemId?: string;
};

export const PageView: FunctionComponent<PageViewProps> = ({
  pageData,
  pageType,
  itemId,
}) => {
  const { sendBIEvent } = useBI();
  const router = useRouter();
  const { prePageUrl } = useHistoryContext();
  const { locale } = router;

  const sendPageViewEvent = useCallback(async (): Promise<void> => {
    const emptyPageData = Object.keys(pageData).length === 0;
    switch (pageType) {
      case PAGES.ARTICLE:
        if (itemId && pageData?.article) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.ARTICLE,
              kb_lang: locale as string,
              item_id: itemId,
              item_type: mapArticleTypeToItemType(pageData.article.type),
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.SEARCH_RESULTS:
        if (
          pageData?.searchResults &&
          pageData?.searchResults.items?.length > 0
        ) {
          const term = new URLSearchParams(location.search).get('term') || '';
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
              kb_lang: locale as string,
              question: term as string,
              presented_content: JSON.stringify(
                pageData?.searchResults.items.map((article, index) => ({
                  order: index + 1,
                  item_id: article.id,
                  locale,
                  type: mapArticleTypeToItemType(article.type),
                }))
              ),
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.CATEGORY:
        if (itemId && pageData.category) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.CATEGORY,
              kb_lang: locale as string,
              item_id: itemId,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.HOMEPAGE:
        if (emptyPageData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.HOME,
              kb_lang: locale as string,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.TICKET:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.TICKET,
              kb_lang: locale as string,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.TICKET,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.PROFILE:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.PROFILE,
              kb_lang: locale as string,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.PROFILE,
              referral_url: prePageUrl,
            })
          );
        }
        break;
      case PAGES.ROADMAP:
        if (pageData.roadmapData) {
          await sendBIEvent(
            pageView({
              source_name: BI.SOURCE_NAMES.ROADMAP,
              kb_lang: locale as string,
              item_id:
                pageData.roadmapData?.label?.id === ALL_PRODUCTS_LABEL_ID
                  ? undefined
                  : (pageData.roadmapData?.label?.id as string),
              tab: tabToName(
                pageData.roadmapData?.resolution as RoadmapFeatureResolution
              ),
              sort_by: sortTypeToName(
                pageData.roadmapData?.sortType as ArticleSortType
              ),
              filter_by:
                FilterToName[
                  pageData.roadmapData?.filterType as RoadmapFilterType
                ],
              referral_url: prePageUrl,
            })
          );
        }
        break;
      default:
        break;
    }
  }, [locale, pageData, pageType, prePageUrl, itemId]);

  useEffect(() => {
    void (async () => {
      await sendPageViewEvent();
    })();
  }, [pageType, itemId]);

  return <></>;
};
